/**
 * Build visual ordering of scenario nodes by traversing from StartNode
 * This helps LLM understand actual node positions like "first", "last", "second-to-last"
 *
 * @param {Object} scenario - Scenario object
 * @returns {Array} - Array of node IDs in visual order
 */
export function buildVisualNodeOrder(scenario) {
  if (!scenario || typeof scenario !== 'object') {
    return [];
  }

  const visualOrder = [];
  const visited = new Set();

  // Find StartNode
  const startNode = Object.values(scenario).find(node => node?.type === 'StartNode');
  if (!startNode) {
    console.warn('[buildVisualNodeOrder] No StartNode found');
    return [];
  }

  // Traverse from StartNode following nextNodes
  let currentNodeId = Object.keys(scenario).find(id => scenario[id] === startNode);

  while (currentNodeId && !visited.has(currentNodeId)) {
    visited.add(currentNodeId);
    visualOrder.push(currentNodeId);

    const currentNode = scenario[currentNodeId];

    // Move to next node (take first nextNode)
    if (currentNode?.nextNodes && currentNode.nextNodes.length > 0) {
      currentNodeId = currentNode.nextNodes[0];
    } else {
      break; // No more nodes
    }
  }

  return visualOrder;
}

/**
 * Find all nodes that can be used as conditionSource for ADD_BRANCH
 * Only choice questions (message:single, message:multi) with response options are supported
 *
 * @param {Object} scenario - Scenario object
 * @returns {Array} - Array of { id, type, label, options } for branchable nodes
 */
export function findBranchableNodes(scenario) {
  if (!scenario || typeof scenario !== 'object') {
    return [];
  }

  const branchableNodes = [];

  for (const [nodeId, node] of Object.entries(scenario)) {
    // Check if node is a choice question with response options
    // nodeType is stored in node.data.nodeType
    const nodeType = node?.data?.nodeType || '';
    const hasResponseOptions = Array.isArray(node?.data?.response) && node.data.response.length > 0;

    if ((nodeType === 'message:single' || nodeType === 'message:multi') && hasResponseOptions) {
      branchableNodes.push({
        id: nodeId,
        type: nodeType,
        label: node.data?.label || node.data?.message || 'Unlabeled Question',
        options: node.data.response.map(opt => opt.value || opt.label)
      });
    }
  }

  return branchableNodes;
}

export function buildSnapshot(scenario, tasks = []) {
  return {
    scenario: JSON.parse(JSON.stringify(scenario)),
    visualOrder: buildVisualNodeOrder(scenario),
    branchableNodes: findBranchableNodes(scenario),
    availableTasks: tasks.map((task) => ({ id: task.id, title: task.title, nodeType: `task:${task.id}` })),
  };
}
